<template>
  <v-sheet color="grey lighten-4 width-100 height-100 pa-4" rounded>
    <div class="d-flex flex-column">
      <div class="d-flex align-center mb-3">
        <v-text-field v-model="idea" label="Describe your game's look" outlined dense hide-details class="mr-2" />
        <v-select v-model="provider" :items="providers" label="Model" dense outlined hide-details class="mr-2" />
        <v-btn color="primary" small :loading="loading" @click="createManifest">Create Manifest</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="success" small :disabled="!hasItems" :loading="batching" @click="batchGenerate">Generate All</v-btn>
      </div>

      <v-alert v-if="error" type="error" dense text class="mb-3">{{ error }}</v-alert>

      <v-row>
        <v-col cols="12" md="4">
          <h4 class="text-subtitle-2 mb-2">Backgrounds</h4>
          <v-list dense two-line>
            <v-list-item v-for="(b, i) in manifest.backgrounds" :key="'bg-'+i">
              <v-list-item-content>
                <v-list-item-title>{{ b.name }}</v-list-item-title>
                <v-list-item-subtitle class="truncate">{{ b.prompt }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="generateOne('image', b)"><v-icon>mdi-image</v-icon></v-btn>
                <v-btn icon small @click="openUpload('backgrounds')"><v-icon>mdi-upload</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
        <v-col cols="12" md="4">
          <h4 class="text-subtitle-2 mb-2">Sprites</h4>
          <v-list dense two-line>
            <v-list-item v-for="(s, i) in manifest.sprites" :key="'sp-'+i">
              <v-list-item-content>
                <v-list-item-title>{{ s.name }}</v-list-item-title>
                <v-list-item-subtitle class="truncate">{{ s.prompt }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="generateOne('image', s)"><v-icon>mdi-image-outline</v-icon></v-btn>
                <v-btn icon small @click="openUpload('sprites')"><v-icon>mdi-upload</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
        <v-col cols="12" md="4">
          <h4 class="text-subtitle-2 mb-2">Sound FX</h4>
          <v-list dense two-line>
            <v-list-item v-for="(f, i) in manifest.sfx" :key="'fx-'+i">
              <v-list-item-content>
                <v-list-item-title>{{ f.name }}</v-list-item-title>
                <v-list-item-subtitle class="truncate">{{ f.prompt }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="generateOne('audio', f)"><v-icon>mdi-volume-high</v-icon></v-btn>
                <v-btn icon small @click="openUpload('sfx')"><v-icon>mdi-upload</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>

      <input ref="fileInput" type="file" class="d-none" @change="doUpload" />
    </div>
  </v-sheet>
</template>

<script>
import { getIdToken } from 'firebase/auth';
import { app as firebaseApp } from '@/firebase/init';

export default {
  name: 'GameAssets',
  props: { game: { type: Object, required: true } },
  data() {
    return {
      idea: '',
      provider: 'openai',
      providers: [
        { text: 'GPT-5', value: 'openai' },
        { text: 'Gemini', value: 'gemini' },
        { text: 'Claude', value: 'claude' }
      ],
      manifest: { backgrounds: [], sprites: [], sfx: [] },
      loading: false,
      batching: false,
      error: '',
      uploadKind: 'uploads'
    };
  },
  computed: {
    hasItems() {
      return (this.manifest.backgrounds?.length || 0) + (this.manifest.sprites?.length || 0) + (this.manifest.sfx?.length || 0) > 0;
    },
    baseUrl() {
      const region = 'us-central1';
      const projectId = firebaseApp.options.projectId;
      return `https://${region}-${projectId}.cloudfunctions.net`;
    },
  },
  methods: {
    async authHeader() {
      const token = await getIdToken(this.$root.user);
      return { Authorization: `Bearer ${token}` };
    },
    async createManifest() {
      if (!this.idea) return;
      this.loading = true; this.error = '';
      try {
        const res = await fetch(`${this.baseUrl}/sparkyCreateManifest`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...(await this.authHeader()) },
          body: JSON.stringify({ prompt: this.idea, provider: this.provider })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Manifest failed');
        this.manifest = json.manifest || { backgrounds: [], sprites: [], sfx: [] };
      } catch (e) { this.error = e.message; }
      finally { this.loading = false; }
    },
    async generateOne(type, item) {
      this.error = '';
      try {
        const res = await fetch(`${this.baseUrl}/sparkyGenerateAsset`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...(await this.authHeader()) },
          body: JSON.stringify({ type, prompt: item.prompt, provider: this.provider, options: type==='image'? { size: item.size || '1024x1024' } : { text: item.prompt, format: 'mp3' } })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Generate failed');
        this.$emit('asset-generated', { type, item, result: json });
      } catch (e) { this.error = e.message; }
    },
    async batchGenerate() {
      if (!this.hasItems) return;
      this.batching = true; this.error = '';
      try {
        const createRes = await fetch(`${this.baseUrl}/sparkyCreateAssetJob`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...(await this.authHeader()) },
          body: JSON.stringify({ gameId: this.game.id, manifest: this.manifest, provider: this.provider })
        });
        const createJson = await createRes.json();
        if (!createRes.ok) throw new Error(createJson.error || 'Job create failed');

        const runRes = await fetch(`${this.baseUrl}/sparkyRunAssetJob`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...(await this.authHeader()) },
          body: JSON.stringify({ jobId: createJson.jobId })
        });
        const runJson = await runRes.json();
        if (!runRes.ok) throw new Error(runJson.error || 'Job run failed');
        this.$emit('assets-batch-complete', runJson);
      } catch (e) { this.error = e.message; }
      finally { this.batching = false; }
    },
    openUpload(kind) {
      this.uploadKind = kind;
      this.$refs.fileInput.click();
    },
    async doUpload(e) {
      const file = e.target.files[0]; if (!file) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('gameId', this.game.id);
        formData.append('kind', this.uploadKind);
        const res = await fetch(`${this.baseUrl}/sparkyUploadAsset`, { method: 'POST', headers: { ...(await this.authHeader()) }, body: formData });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Upload failed');
        this.$emit('asset-uploaded', json);
      } catch (e) { this.error = e.message; }
      finally { this.$refs.fileInput.value = ''; }
    }
  }
};
</script>

<style scoped>
.truncate { max-width: 280px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
</style>
